import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { decryptText } from "../../utils/encryptionUtils";
import publisherService from "../../services/generalApi/publisher.service";
import { IGetPostPublisherDto } from "../../models/dto/application/IPostInputs";
import ImageService from "../../services/generalApi/image.service";
import { IPublisherDto } from "../../models/dto/generalApi/IPublisherDto";
import groupBy from "../../utils/conversion";

const useHomeViewModel = () => {
  const [newPublishers, setNewPublishers] = useState<any[][]>([]);
  const [showLoading, setShowLoading] = useState(false);
  const [openCreatePost, setOpenCreatePost] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePostID, setDeletePostID] = useState(0);
  const [updatePostID, setUpdatePostID] = useState(0);
  const [isUpdate, setIsUpdate] = useState(false);
  const user: any = useSelector((state: RootState) => state.user);
  const userName = decryptText(user.user?.name);
  const userSurname = decryptText(user.user?.lastName);
  const userRoleIdLogged = Number(decryptText(user.user?.roleId));
  const userFullName = `${userName} ${userSurname}`;
  const canCreatePostIds = [1, 3, 16, 17, 19, 30];

  const setCarouselElements = async (
    publisherArray: IGetPostPublisherDto[]
  ) => {
    try {
      setShowLoading(false);

      const downloadBlob = async (url: string, type: "image" | "pdf") => {
        if (url && url !== "") {
          const response =
            type === "image"
              ? await ImageService.getBlobImage(url)
              : await ImageService.getBlobPdf(url);
          if (response instanceof Blob) {
            return URL.createObjectURL(response);
          } else {
            console.error("Error: Invalid response type");
            return null;
          }
        }
        return null;
      };

      const processPublisher = async (publisher: IGetPostPublisherDto) => {
        const imagePromise = downloadBlob(publisher.imageURL, "image");
        const pdfPromise = downloadBlob(publisher.fileURL, "pdf");

        const [imageUrl, fileUrl] = await Promise.all([
          imagePromise,
          pdfPromise,
        ]);

        if (imageUrl) publisher.imageURL = imageUrl;
        if (fileUrl) publisher.fileURL = fileUrl;

        return publisher;
      };

      const processedPublishers = await Promise.all(
        publisherArray.map((publisher) => processPublisher(publisher))
      );

      const newPublishersArray: IPublisherDto[] = processedPublishers.reduce(
        (acc: IPublisherDto[], publisher) => {
          if (publisher.imageURL) {
            acc.push(
              getPublisherObject(publisher, "image")
            );
          }
          if (publisher.linkURL) {
            acc.push(
              getPublisherObject(publisher, "video")
            );
          }
          if (publisher.fileURL) {
            acc.push(getPublisherObject(publisher, "pdf"));
          }
          return acc;
        },
        []
      );

      const groupData: any[][] = Object.values(
        groupBy(newPublishersArray, "postID")
      );

      // sort by postID descending
      groupData.sort((a, b) => {
        return b[0].postID - a[0].postID;
      });

      setNewPublishers(groupData);
    } catch (e) {
      console.error("error", e);
    } finally {
      setShowLoading(true);
    }
  };

  const getPublisherObject = (
    publisher: IGetPostPublisherDto,
    type: string
  ) => {
    const newPublisherParse: IPublisherDto = {
      postID: publisher.postID,
      postTitle: publisher.postTitle,
      postOwner: publisher.postOwner,
      datePost: "",
      imageURL: publisher.imageURL,
      videoURL: publisher.videoURL,
      fileURL: publisher.fileURL,
      postDescription: publisher.postDescription,
      countReactions: publisher.countReactions,
      userReaction: publisher.userReaction,
      type: type,
      linkURL: publisher.linkURL,
    };

    newPublisherParse.type = type;
    return newPublisherParse;
  };

  const getPublishers = (isFirstTime: boolean) => {
    if (isFirstTime) {
      publisherService.getPublisherById().then((r: IGetPostPublisherDto[]) => {
        setCarouselElements(r);
      });
    } else {
      publisherService
        .getPublisherById()
        .then((r: any) => {
          setCarouselElements(r);
        })
        .catch((e: any) => {
          console.log("error", e);
        });
    }
  };

  const handleOpenCreatePost = (value: boolean) => {
    setOpenCreatePost(value);
    if (!value) {
      setIsUpdate(false);
      setUpdatePostID(0);
    }
  };

  const handleDeleteButtonClick = (postID: number) => {
    setDeletePostID(postID);
    setShowDeleteModal(true);
  };

  const handleDeletePost = (postID: number) => {
    publisherService.deletePost(postID).then(() => {
      setShowDeleteModal(false);
      handleCloseCreatePost();
      getPublishers(false);
    });
  };

  const handleCloseCreatePost = () => {
    setShowDeleteModal(false);
    setDeletePostID(0);
  };

  const handleSetLoading = (value: boolean) => {
    setShowLoading(value);
  };

  const handleSetIsUpdate = (value: boolean) => {
    setIsUpdate(value);
  };

  const handleSetUpdatePostID = (postID: number) => {
    setUpdatePostID(postID);
  };

  const handlePublisher = () => {
    getPublishers(false);
  };

  return {
    newPublishers,
    showLoading,
    openCreatePost,
    showDeleteModal,
    deletePostID,
    updatePostID,
    isUpdate,
    userRoleIdLogged,
    userFullName,
    canCreatePostIds,
    getPublishers,
    handleOpenCreatePost,
    handleSetLoading,
    handleSetIsUpdate,
    handleSetUpdatePostID,
    handleDeleteButtonClick,
    handleDeletePost,
    handleCloseCreatePost,
    handlePublisher,
  };
};

export default useHomeViewModel;
